import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import FileDownload from 'js-file-download';
import { Button, Divider, Modal, Search, Typography } from '@telus-uds/components-web';

import Filters from '../components/filters';
import { Button as StyledButton, ButtonWrapper, ExportButton, SearchWrapper } from './css';
import { ContentWrapper, Box } from '../../design/css';
import Table from '../../design/shared/table';
import request, {
  apiHost,
  USER_RIGHTS,
  config as requestConfig,
  isSalesManager,
  isOrderFulfillmentTeam,
  isSalesSupport,
  canSubmitOrders
} from '../../utils/request';

import OrderListModel, {
  OrderHeaderModel,
  getOptions,
  RepModel,
  ChannelModel,
  DealershipModel
} from './models';
import OrderDetail from '../orderDetails';
import MessageNotification from '../../common/messageNotification';
import ValidateLoader from '../../UI/validateLoader';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { hasLength, getFormattedDate } from '../../utils/utils';
import {
  getBusinessAppFormStepFields,
  contentfulFormConfig
} from '../../contentful';
import {
  CONSTANT,
  orderStatus,
  quoteStatus,
  keyViewOrder,
  orderDetailsForm,
  keyContractResend,
  keyOrderReturn
} from '../../utils/constant';

import { getStatus, getLink } from '../../srtDashboard/utils';
import { DisabledAction } from '../../salesRepDashboard/css';

const connectionKey = 'BUSINESS_APP_ORDERS';

const initialFilterState = {
  productInOrderFor: [],
  status: [],
  createdBy: [],
  province: [],
  channelId: [],
  dealership: [],
  endDateOpenDate: null,
  endDateSubmissionDate: null,
  startDateOpenDate: null,
  startDateSubmissionDate: null,
  filterCriteria: null
};

const Orders = ({ locale }) => {
  const history = useHistory();
  const localizedStrings = useLocalizedStringsContext();
  const staticLabels = localizedStrings[connectionKey];
  const {
    general: { loading: loadingLabel },
    srtDashboard: { locked }
  } = localizedStrings;
  const {
    EXPORT,
    NO_ORDERS,
    LOADING_MESSAGE,
    ORDER_WITH,
    SUCCESS_MESSAGE,
    DELETE_MESSAGE,
    viewOrderButtonLabel,
    ordersTableTitle,
    proceedWithOrder,
    deleteOrderLabel,
    modifyQuote,
    confirmationDialog,
    confirmationMessage,
    cancel,
    confirm,
    searchLabel,
    exportWarning,
    searchHint,
    requestOrderReturnButtonLabel,
    contractResendButtonLabel
  } = staticLabels;
  const {
    KEYS: {
      ROLES: { ORDER_FULFILLMENT }
    }
  } = CONSTANT;

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(false);
  const [isExport, setIsExport] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [filterState, setFilterState] = useState(initialFilterState);
  const config = { ...contentfulFormConfig, locale };
  const {
    endDateOpenDate,
    endDateSubmissionDate,
    startDateOpenDate,
    startDateSubmissionDate,
    assignee,
    channelId,
    dealership,
    province,
    createdBy,
    filterCriteria,
    status,
    productInOrderFor
  } = filterState;
  const {
    incomplete,
    contractSent,
    contractSigned,
    orderSubmitted,
    contractExpired,
    orderCanceled
  } = orderStatus;
  const { approved } = quoteStatus;

  const [viewOrderId, setViewOrderId] = useState();
  const [data, setData] = useState();
  const [orderDetails, setOrderDetails] = useState(null);
  const [isOrderUpdated, setOrderUpdated] = useState();
  const [orderSummary, setOrderSummary] = useState(null);
  const [order, setOrder] = useState({});
  const [action, setAction] = useState();
  const [salesRepNames, setSalesRepNames] = useState(null);
  const [assignees, setAssignees] = useState(null);
  const [channels, setChannels] = useState(null);
  const [dealerships, setDealerships] = useState(null);
  const [orderComments, setOrderComments] = useState(null);
  const [tableHeaders, setTableHeaders] = useState(
    new OrderHeaderModel(staticLabels)
  );
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState();
  const [message, setMessage] = useState(SUCCESS_MESSAGE);
  const profile = request.getProfile();
  const userProfileRole = profile && profile.role.name;
  const userRights = userProfileRole && USER_RIGHTS[userProfileRole];
  const orderRequestRef = useRef(null);

  const pageSize = 50;
  const pageQuery = `pageNumber=${page || 1}&pageSize=${pageSize}`;
  const isOrderCancelAvailable =
    profile && userProfileRole === ORDER_FULFILLMENT;

  const allowedStatusForDashboardSOV = [
    incomplete,
    contractSent,
    contractSigned,
    orderCanceled,
    contractExpired,
    approved,
    orderSubmitted
  ];

  const allowedStatusForDashboardOrderFulfillment = [
    contractSent,
    contractSigned,
    orderCanceled,
    contractExpired,
    approved,
    orderSubmitted
  ];

  const getStatusQuery = () => {
    let statusArr;
    if (isOrderFulfillmentTeam)
      statusArr = allowedStatusForDashboardOrderFulfillment;
    else statusArr = allowedStatusForDashboardSOV;

    return statusArr
      .reduce((acc, curr) => {
        acc.push(`status=${curr}`);
        return acc;
      }, [])
      .join('&');
  };

  let ordersUrl = `/orders?type=BA&${pageQuery}&${getStatusQuery()}`;

  // SOF-3356 T037 — broaden Business Apps OFD listing to the SM's full
  // dealership when the SM has dealership-wide visibility enabled. Backend
  // re-checks authorisation and silently ignores the parameter otherwise.
  if (
    isSalesManager &&
    profile &&
    profile.dealershipVisibilityEnabled === true
  ) {
    const smDealershipName = _.get(profile, 'Dealership.name', '');
    if (smDealershipName) {
      ordersUrl += `&dealershipName=${encodeURIComponent(smDealershipName)}`;
    }
  }
  const isFilterApplied = hasLength(
    endDateOpenDate,
    endDateSubmissionDate,
    startDateOpenDate,
    startDateSubmissionDate,
    assignee,
    channelId,
    dealership,
    province,
    createdBy,
    filterCriteria,
    status,
    productInOrderFor
  );

  const filterData = {
    salesRepNames,
    assignees,
    channels,
    dealerships
  };

  useEffect(() => {
    setTableHeaders(new OrderHeaderModel(staticLabels));
  }, [localizedStrings]);

  const fetchDataFromUrl = url => request.get(url);

  const loadOrders = (url, payload) => {
    if (orderRequestRef.current) orderRequestRef.current.cancel();
    const currentRequest = axios.CancelToken.source();
    orderRequestRef.current = currentRequest;
    const options = { ...requestConfig(), cancelToken: currentRequest.token };
    setListError(false);
    setLoading(true);
    const pendingRequest = payload
      ? axios.post(`${apiHost}${url}`, payload, options)
      : axios.get(`${apiHost}${url}`, options);
    return pendingRequest
      .then(({ data: { results: orders, totalCount } }) => {
        if (orderRequestRef.current !== currentRequest) return;
        setData({ orders, count: totalCount });
        setLoading(false);
      })
      .catch(error => {
        if (
          !axios.isCancel(error) &&
          orderRequestRef.current === currentRequest
        ) {
          setData({ orders: [], count: 0 });
          setListError(true);
          setLoading(false);
        }
      });
  };

  const fetchData = () => loadOrders(ordersUrl);

  const getFilterPayload = (pageNumber = page) => {
    const payload = {
      ...filterState,
      pageNumber,
      pageSize,
      province: filterState.province.map(row => row.value),
      status: filterState.status.map(row => row.value),
      createdBy: filterState.createdBy.map(row => row.value),
      productInOrderFor: filterState.productInOrderFor.map(row => row.value),
      channelId:
        isSalesSupport &&
        !filterState.channelId.length &&
        profile.channels.length
          ? profile.channels.map(row => row.id)
          : filterState.channelId.map(row => row.value),
      type: 'BA',
      dealership: filterState.dealership.map(row => row.value)
    };
    delete payload.assignee;

    if (!filterState.status.length) {
      const statusFilters = isOrderFulfillmentTeam
        ? allowedStatusForDashboardOrderFulfillment
        : allowedStatusForDashboardSOV;
      payload.status = [...statusFilters];
    }
    if (filterState.startDateOpenDate)
      payload.startDate = getFormattedDate(filterState.startDateOpenDate);

    if (filterState.endDateOpenDate)
      payload.endDate = getFormattedDate(filterState.endDateOpenDate);

    if (filterState.startDateSubmissionDate)
      payload.startDate = getFormattedDate(filterState.startDateSubmissionDate);

    if (filterState.endDateSubmissionDate)
      payload.endDate = getFormattedDate(filterState.endDateSubmissionDate);

    if (filterState.startDateOpenDate || filterState.endDateOpenDate)
      payload.filterCriteria = 'createdAt';

    if (
      filterState.startDateSubmissionDate ||
      filterState.endDateSubmissionDate
    )
      payload.filterCriteria = 'validatedAt';

    delete payload.startDateOpenDate;
    delete payload.endDateOpenDate;
    delete payload.startDateSubmissionDate;
    delete payload.endDateSubmissionDate;
    return payload;
  };

  const getSearchPayload = pageNumber => ({
    pageNumber,
    pageSize,
    type: 'BA',
    status: [
      ...(isOrderFulfillmentTeam
        ? allowedStatusForDashboardOrderFulfillment
        : allowedStatusForDashboardSOV)
    ]
  });

  const filterOrders = (
    isResetMode = false,
    pageNumber = page,
    search = searchString
  ) => {
    if (isResetMode) {
      setSearchString('');
      const params = new URLSearchParams(ordersUrl.split('?')[1]);
      params.set('pageNumber', pageNumber);
      return loadOrders(`/orders?${params.toString()}`);
    }
    const query = search && search.trim();
    // A search spans every dashboard status and ignores the filter panel,
    // so an order is found regardless of the tab/filters currently applied.
    const payload = query
      ? { ...getSearchPayload(pageNumber), q: query }
      : getFilterPayload(pageNumber);
    if (
      isSalesManager &&
      profile &&
      profile.dealershipVisibilityEnabled === true
    ) {
      const dealershipName = _.get(profile, 'Dealership.name', '');
      if (dealershipName) payload.dealershipName = dealershipName;
    }
    return loadOrders('/orders/filter', payload);
  };

  const updateFilters = (key, value) => {
    setFilterState({ ...filterState, [key]: value });
  };

  const onClickView = id => {
    setLoading(true);
    getBusinessAppFormStepFields(config, setOrderSummary);
    Promise.all([
      fetchDataFromUrl(`/orders/${id}`),
      fetchDataFromUrl(`/orders/${id}/comments`)
    ]).then(([orderResp, commentsResp]) => {
      const { data: orders } = orderResp;
      const {
        data: { results: comments }
      } = commentsResp;
      setOrderDetails(orders);
      setOrderComments(comments);
      setOrderUpdated(false);
      setLoading(false);
    });
  };

  const openOrder = id => {
    setOrderDetails(null);
    setViewOrderId(id);
  };

  const doExport = () => {
    const payload = getFilterPayload();
    setAction();
    setExportError(null);
    setIsExport(true);
    axios
      .post(`${apiHost}/orders/export/excel`, payload, {
        ...requestConfig(),
        responseType: 'arraybuffer'
      })
      .then(response => response.data)
      .then(fileData => {
        setIsExport(false);
        FileDownload(fileData, 'orders.xlsx');
      })
      .catch(err => {
        setExportError(err && err.message ? err.message : 'Failed to export orders. Please try again.');
        setIsExport(false);
      });
  };

  const onClickExport = () => {
    if (!isExport) {
      if (!isFilterApplied) {
        setAction(EXPORT);
      } else {
        doExport();
      }
    }
  };

  const updateContractResendToTrue = row => {
    const body = {
      isContractResend: true
    };
    setLoading(true);
    request.put(`/orders/${row.id}`, body).then(() => {
      setOrder({ ...order, open: false, isUpdated: true });
      setLoading(false);
    });
  };

  const updateOrderReturnToTrue = row => {
    const body = {
      isOrderReturned: true
    };
    setLoading(true);
    request.put(`/orders/${row.id}`, body).then(() => {
      setOrder({ ...order, open: false, isUpdated: true });
      setLoading(false);
    });
  };

  const onSave = value => {
    setMessage(SUCCESS_MESSAGE);
    setOrderUpdated(value);
  };

  const resetFilters = () => {
    setFilterState({
      ...initialFilterState
    });
  };

  const actionsSalesManager = [
    {
      key: keyViewOrder,
      label: viewOrderButtonLabel,
      onClick: row => openOrder(row.id),
      forStatus: [
        'quoteApproved',
        'contractSent',
        'contractSigned',
        'orderCanceled',
        'orderSubmitted',
        'contractExpired',
        'orderIncomplete'
      ]
    }
  ];

  const getActionsForSalesManager = row => {
    const { id, isLockedBy, isOrderReturned, isContractResend } = row;
    const { statusId } = getStatus(row);
    const { email } = profile;

    if (isLockedBy && isLockedBy !== email)
      return <p style={{ fontSize: '14px', color: '#adadad' }}>{locked}</p>;

    let actionsList;

    if (isLockedBy && isLockedBy !== email)
      actionsList = actionsSalesManager.filter(
        ({ key }) => key === keyViewOrder
      );
    else
      actionsList = actionsSalesManager.filter(({ forStatus }) =>
        forStatus.includes(statusId)
      );

    // SOF-3356 T038 — when an SM with dealership-wide visibility is viewing
    // a row submitted by another user in the same dealership, restrict the
    // BA OFD action list to view-only. Backend (T035) is source of truth.
    if (
      isSalesManager &&
      profile &&
      profile.dealershipVisibilityEnabled === true
    ) {
      const rowSubmitterId = _.get(row, 'userId', _.get(row, 'user_id'));
      if (rowSubmitterId && rowSubmitterId !== profile.id) {
        actionsList = actionsList.filter(({ key }) => key === keyViewOrder);
      }
    }

    // SOF-3356 Phase 9 T058 — defence-in-depth UX lock. Side-effect of T054:
    // SMs without `canSubmitOrders` can now reach the BA OFD listing and
    // may have historical own-submitted orders, reporting reps' orders, or
    // dealership-wide rows visible. Per stakeholder feedback (2026-05-05),
    // every mutating affordance MUST be suppressed for these users on
    // EVERY row regardless of ownership / status / lock state — only View
    // remains. Backend FR-B024 mutation gate is the authoritative boundary;
    // this is UI hygiene to keep the user out of dead-ends.
    if (isSalesManager && !canSubmitOrders()) {
      actionsList = actionsList.filter(({ key }) => key === keyViewOrder);
    }

    return actionsList.map(({ label, onClick }, index) => {
      const key = `order${id}actionLink${index}`;
      if (isOrderReturned && label === requestOrderReturnButtonLabel) {
        return <DisabledAction>{label}</DisabledAction>;
      }
      if (isContractResend && label === contractResendButtonLabel) {
        return <DisabledAction>{label}</DisabledAction>;
      }
      if (onClick)
        return (
          <StyledButton
            className="action-button"
            key={key}
            onClick={() => onClick(row)}
          >
            {label}
          </StyledButton>
        );
      return getLink(id, label);
    });
  };

  const getActions = row => {
    const { status: currentStatus, id } = row;

    return (
      <>
        <StyledButton
          key={id}
          className="action-button"
          onClick={() => openOrder(row.id)}
        >
          {viewOrderButtonLabel}
        </StyledButton>
        {isOrderCancelAvailable &&
          [contractSent, contractSigned].includes(currentStatus) && (
            <StyledButton
              key={id}
              className="action-button"
              onClick={() => {
                setAction(deleteOrderLabel);
                setOrder({ open: true, orderDetails: row });
              }}
            >
              {deleteOrderLabel}
            </StyledButton>
          )}
      </>
    );
  };

  const redirect = (row, formName, step) => {
    history.push({
      pathname: `/${row.locale}/${row.categoryName}/${formName}`,
      search: `?orderId=${row.id}&step=${step}`
    });
    history.go();
  };

  const deleteOrder = () => {
    return request.delete(`/orders/${order.orderDetails.id}`).then(() => {
      setOrder({ ...order, open: false, isUpdated: true });
      setOrderUpdated({ orderId: order.orderDetails.orderId, isUpdated: true });
      setMessage(DELETE_MESSAGE);
      fetchData();
    });
  };

  const onConfirm = () => {
    switch (action) {
      case modifyQuote:
        redirect(order.orderDetails, orderDetailsForm, 1);
        break;
      case proceedWithOrder:
        redirect(order.orderDetails, orderDetailsForm, 2);
        break;
      case deleteOrderLabel:
        deleteOrder();
        break;
      case EXPORT:
        doExport();
        break;
      case keyOrderReturn:
        updateOrderReturnToTrue(order.orderDetails);
        break;
      case keyContractResend:
        updateContractResendToTrue(order.orderDetails);
        break;
      default:
    }
  };

  useEffect(() => {
    if (isOrderUpdated) {
      setOrderUpdated(false);
    }
  }, [
    endDateOpenDate,
    endDateSubmissionDate,
    startDateOpenDate,
    startDateSubmissionDate
  ]);

  // Remove the two separate useEffect hooks and replace with one consolidated one
  useEffect(() => {
    // Only fetch data if we're not viewing order details
    if (!orderDetails) {
      // If we have a viewOrderId, handle order viewing
      if (viewOrderId) {
        onClickView(viewOrderId);
      }
      // Otherwise, fetch orders based on current state
      else if (isFilterApplied || searchString) {
        filterOrders();
      } else {
        fetchData();
      }
    }
  }, [orderDetails, page, viewOrderId, isOrderUpdated]);

  useEffect(() => {
    Promise.allSettled([
      fetchDataFromUrl('/salesRepUsers'),
      fetchDataFromUrl('/sovAgentAndAdminUsers'),
      fetchDataFromUrl('/channels'),
      fetchDataFromUrl('/dealerships')
    ]).then(
      ([
        // usersResp,
        salesRepNameResp,
        assigneeResponse,
        channelsResp,
        dealershipsResp
      ]) => {
        const {
          value: { data: repNames }
        } = salesRepNameResp;
        const {
          value: { data: assigneeData }
        } = assigneeResponse;
        const {
          value: {
            data: { channels: channelList }
          }
        } = channelsResp;
        const {
          value: {
            data: { rows: listOfDealerships }
          }
        } = dealershipsResp;

        setSalesRepNames(
          repNames.map(item => ({ label: item.name, id: item.email }))
        );
        setAssignees(getOptions(assigneeData, RepModel));
        setChannels(getOptions(channelList, ChannelModel));
        setDealerships(getOptions(listOfDealerships, DealershipModel));
      }
    );
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (orderRequestRef.current) {
        orderRequestRef.current.cancel();
        orderRequestRef.current = null;
      }
    };
  }, []);

  return (
    <ContentWrapper>
      {loading ? (
        <ValidateLoader message={LOADING_MESSAGE} />
      ) : (
        <>
          {viewOrderId ? (
            <>
              {!orderDetails ? (
                <ValidateLoader message={LOADING_MESSAGE} />
              ) : (
                <>
                  {orderSummary && userRights && (
                    <OrderDetail
                      open={!!orderDetails}
                      setOpen={() => setViewOrderId('')}
                      order={orderDetails}
                      onSave={onSave}
                      userRights={userRights}
                      assignees={assignees}
                      comments={orderComments}
                      summary={orderSummary}
                      isSalesManager={isSalesManager}
                      isSalesSupport={isSalesSupport}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Box bottom={7}>
                <Filters
                  updateFilters={(key, value) => updateFilters(key, value)}
                  filterState={filterState}
                  onApplyFilters={isResetMode => filterOrders(isResetMode, 1)}
                  onResetFilters={resetFilters}
                  data={filterData}
                  staticLabels={staticLabels}
                  setPage={setPage}
                />
              </Box>
              <Divider space={1} variant={{ inverse: true }} />
              {isOrderUpdated && isOrderUpdated.isUpdated && (
                <Box top={4} bottom={4}>
                  <MessageNotification
                    variant="success"
                    message={`${ORDER_WITH}${isOrderUpdated.orderId} ${message}`}
                  />
                </Box>
              )}
              <SearchWrapper>
                <div style={{ maxWidth: '400px', width: '100%' }}>
                  <Typography variant={{ size: 'small', weight: 'bold' }} style={{ marginBottom: '8px' }}>
                    {searchLabel}
                  </Typography>
                  <Search
                    label={searchLabel}
                    placeholder={searchHint}
                    value={searchString || ''}
                    onChange={setSearchString}
                    onSubmit={() => {
                      setPage(1);
                      filterOrders(false, 1);
                    }}
                    onClear={() => {
                      setSearchString('');
                      setPage(1);
                      filterOrders(true, 1);
                    }}
                  />
                </div>
              </SearchWrapper>
              {listError && (
                <MessageNotification
                  variant="error"
                  message={
                    locale === 'fr-CA'
                      ? 'Impossible de charger les commandes. Veuillez r\u00e9essayer.'
                      : 'Unable to load orders. Please try again.'
                  }
                />
              )}
              {!listError &&
                (data && data.orders && data.orders.length ? (
                  <>
                    <Table
                      title={ordersTableTitle}
                      header={tableHeaders}
                      data={data.orders}
                      actions={
                        isSalesManager ? getActionsForSalesManager : getActions
                      }
                      dataModel={OrderListModel}
                      currentPage={parseInt(page || 1, 10)}
                      count={data.count}
                      step={pageSize}
                      actionsDisplay="grid"
                      setPage={setPage}
                    />
                    <ButtonWrapper>
                      <ExportButton
                        onClick={onClickExport}
                        className="buttonload"
                      >
                        <i
                          className={
                            isExport
                              ? 'fa  fa-circle-o-notch fa-spin'
                              : 'fa fa-spin'
                          }
                        />
                        {isExport ? loadingLabel : EXPORT}
                      </ExportButton>
                    </ButtonWrapper>
                    {exportError && (
                      <Box top={2} bottom={2}>
                        <MessageNotification variant="error" message={exportError} />
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography heading="h4" variant={{ size: 'h4' }} block>{NO_ORDERS}</Typography>
                ))}
            </>
          )}
        </>
      )}
      <Modal
        isOpen={(action && order.open) || action === EXPORT}
        onClose={() => {
          if (action === EXPORT) {
            setAction();
          } else {
            setOrder({ ...order, open: false });
          }
        }}
        title={confirmationDialog}
      >
        <p>{`${
          action === EXPORT ? exportWarning : ''
        }${confirmationMessage}?`}</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button
            type="button"
            variant={{ priority: 'low' }}
            onPress={() => {
              if (action === EXPORT) {
                setAction();
              } else {
                setOrder({ ...order, open: false });
              }
            }}
          >
            {cancel}
          </Button>
          <Button
            type="button"
            variant={{ priority: 'high' }}
            onPress={() => onConfirm()}
          >
            {confirm}
          </Button>
        </div>
      </Modal>
    </ContentWrapper>
  );
};

Orders.propTypes = {
  locale: PropTypes.string.isRequired
};

export default Orders;
